// frontend/src/components/loi/LoiHouseStyle.tsx
/**
 * His house style in the dialog (spec 3.8): keep a change for next time; see every rule; skip one for this
 * letter; remove one. ⛔ A rule never holds a deal's facts - the server refuses and says which.
 */
import { useState } from 'react';
import { Button } from '../ui';
import { api } from '../../lib/api';
import type { LoiGenerateInput } from '../../types';
import { LOI_SECTION_TITLES, type SectionOverride } from '../../types/loiOverride';
import type { LoiOverridesState } from './useLoiOverrides';

const isAdded = (key: string): boolean => /^custom_\d+$/.test(key);

/**
 * ⛔ A section key is an identifier, never shown to him. Used only when the letter on screen does not have the
 *   section (a rule or a change can name one this letter lacks): D3 - a standard section by its own title from the
 *   fixed map of his fourteen, and an added section as such.
 */
export const sectionInWords = (key: string): string =>
  (LOI_SECTION_TITLES as Record<string, string>)[key] ?? (isAdded(key) ? 'a section you added' : 'a section of the letter');

/**
 * How a change or a house-style rule names what it changes - the same words in "Your changes", the house-style list and
 * a "not applied" line. `onScreen` is the letter's own title for a section it has.
 * ⭐ D2: an added section is "New section after <its anchor>: <its title>" - never the anchor's title alone.
 * ⭐ D3: a rule on a section he added, which this letter lacks, is named by the rule's own title when it has one.
 */
export const changeName = (
  c: { section: string; action: string; title?: string | null },
  onScreen: (key: string) => string | undefined,
): string => {
  const titleOf = (key: string): string => onScreen(key) ?? sectionInWords(key);
  if (c.action === 'add_after') return `New section after ${titleOf(c.section)}: ${c.title || 'a new section'}`;
  if (isAdded(c.section)) return onScreen(c.section) ?? (c.title || sectionInWords(c.section));
  return titleOf(c.section);
};

export function KeepAsHouseStyle({ override, readInput, state }: { override: SectionOverride; readInput: () => LoiGenerateInput | null; state: LoiOverridesState }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (override.source === 'house_style') return null;
  const keep = async () => {
    const input = readInput();
    if (!input) return;
    setBusy(true);
    setError(null);
    try {
      await api.saveLoiHouseStyle(input, {
        section: override.section,
        action: override.action,
        ...(override.title ? { title: override.title } : {}),
        ...(override.paragraphs ? { paragraphs: override.paragraphs } : {}),
        ...(override.newKey ? { newKey: override.newKey } : {}),
        asked: override.asked,
      });
      await state.undo(override.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That change could not be kept.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <Button type="button" variant="ghost" onClick={() => void keep()} disabled={busy}>Always do it this way</Button>
      {error && <span className="text-[12px] text-amber-900">{error}</span>}
    </>
  );
}

export function HouseStyleList({ state }: { state: LoiOverridesState }) {
  const [open, setOpen] = useState(false);
  /** Ruling 22: why a Remove was refused, per rule, in the server's words (a non-admin gets a 403 - Ruling 11). */
  const [refused, setRefused] = useState<Record<string, string>>({});
  const rules = state.preview?.houseStyle ?? [];
  if (rules.length === 0) return null;
  // The letter's own title when it has the section; a rule may name one this letter does not have (D2, D3).
  const onScreen = (key: string): string | undefined => state.preview?.sections?.find((s) => s.key === key)?.title;
  const remove = async (id: string) => {
    setRefused(({ [id]: _was, ...others }) => others);
    try {
      await api.removeLoiHouseStyle(id);
      await state.refresh();
    } catch (e) {
      // ⛔ Never an unhandled rejection, and never a list refreshed as if the rule were gone.
      setRefused((all) => ({ ...all, [id]: e instanceof Error && e.message ? e.message : 'That rule could not be removed.' }));
    }
  };
  return (
    <div className="mt-3" data-testid="loi-house-style">
      <Button type="button" variant="ghost" onClick={() => setOpen((v) => !v)}>{`My house style (${rules.length})`}</Button>
      {open && rules.map((r) => (
        <div key={r.id} data-rule={r.id} className="mt-1 flex flex-wrap items-center gap-2 text-[12px]">
          <span>{changeName(r, onScreen)}: "{r.asked}"{r.skipped ? ' - standard wording this time' : ''}</span>
          {r.skipped
            ? <Button type="button" variant="ghost" onClick={() => void state.setSkip(r.id, false)}>Use it again</Button>
            : <Button type="button" variant="ghost" onClick={() => void state.setSkip(r.id, true)}>Standard wording this time</Button>}
          <Button type="button" variant="ghost" onClick={() => void remove(r.id)}>Remove</Button>
          {refused[r.id] && <span className="text-[12px] text-amber-900">{refused[r.id]}</span>}
        </div>
      ))}
    </div>
  );
}
